/* 列傳模式的野戰地圖產生器
 * 和赤壁（core/map.ts）分開寫：不對稱、比赤壁大、地形種類多。產生的物件介面和赤壁相同（SG.MAP 的欄位），
 * 引擎、AI、繪圖都直接沿用；用 SG.useMap(fieldMap(def)) 換上，離開列傳時 SG.useMap() 換回赤壁。
 *
 * 地形（每 20px 一格）：
 *   林（forest）  擋路、擋視線
 *   石（rock）    山丘岩壁：擋路、擋視線
 *   水（water）   深水：擋路但不擋視線與投射物；路經過的地方自動架橋，渡口（ford）可以涉水
 *   屋（house）   村落房舍、黃巾帳篷：擋路、擋視線
 * 另外是不擋路的：路、農田、沼澤（淺水，算「濕」）、草叢（蘆葦／高草，躲在裡面敵人看不到）。 */
// @ts-nocheck
import { SG } from '../core/sg.ts';

const SC = 20, TY = 1.4, BODY = 18, CS = 40; // 地形格、地面度量的縱向倍率、身體半徑、尋路格
const OPEN = 0, FOREST = 1, ROCK = 2, WATER = 3, HOUSE = 4;
const N4 = [[1, 0], [-1, 0], [0, 1], [0, -1]];
const N8 = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];

function rng(seed) {
  return function () {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function noise(seed, scale) {
  const lat = new Map(), r = rng(seed);
  const hv = (i, j) => { const k = i * 4096 + j; if (!lat.has(k)) lat.set(k, r() * 2 - 1); return lat.get(k); };
  return (x, y) => {
    const fx = x / scale, fy = y / scale, i = Math.floor(fx), j = Math.floor(fy), u = fx - i, v = fy - j, su = u * u * (3 - 2 * u), sv = v * v * (3 - 2 * v);
    return (hv(i, j) * (1 - su) + hv(i + 1, j) * su) * (1 - sv) + (hv(i, j + 1) * (1 - su) + hv(i + 1, j + 1) * su) * sv;
  };
}
function segDist(px, py, a, b) {
  const vx = b[0] - a[0], vy = b[1] - a[1], L = vx * vx + vy * vy;
  let t = L ? ((px - a[0]) * vx + (py - a[1]) * vy) / L : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - a[0] - vx * t, py - a[1] - vy * t);
}
const lineDist = (x, y, pts) => { let d = 1e9; for (let i = 0; i < pts.length - 1; i++) d = Math.min(d, segDist(x, y, pts[i], pts[i + 1])); return d; };
const inEll = (x, y, b) => ((x - b[0]) / b[2]) ** 2 + ((y - b[1]) / b[3]) ** 2 <= 1;
// 折線用 Catmull-Rom 補成平滑曲線（河、路都用），每段切 n 份
function smooth(pts, n = 8) {
  if (pts.length < 3) return pts.map((p) => p.slice());
  const out = [], P = (i) => pts[Math.max(0, Math.min(pts.length - 1, i))];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = P(i - 1), p1 = P(i), p2 = P(i + 1), p3 = P(i + 2);
    for (let s = 0; s < n; s++) {
      const t = s / n, t2 = t * t, t3 = t2 * t;
      out.push([0, 1].map((k) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3)));
    }
  }
  out.push(pts[pts.length - 1].slice());
  return out;
}
// 倒角距離轉換（地面度量）
function chamfer(src, out, SW, SH) {
  const NC = SW * SH, WX = SC, WY = SC * TY, WD = Math.hypot(WX, WY);
  for (let k = 0; k < NC; k++) out[k] = src[k] ? 0 : 1e9;
  for (let j = 0; j < SH; j++) for (let i = 0; i < SW; i++) {
    const k = j * SW + i; let d = out[k];
    if (i > 0) d = Math.min(d, out[k - 1] + WX);
    if (j > 0) { d = Math.min(d, out[k - SW] + WY); if (i > 0) d = Math.min(d, out[k - SW - 1] + WD); if (i < SW - 1) d = Math.min(d, out[k - SW + 1] + WD); }
    out[k] = d;
  }
  for (let j = SH - 1; j >= 0; j--) for (let i = SW - 1; i >= 0; i--) {
    const k = j * SW + i; let d = out[k];
    if (i < SW - 1) d = Math.min(d, out[k + 1] + WX);
    if (j < SH - 1) { d = Math.min(d, out[k + SW] + WY); if (i < SW - 1) d = Math.min(d, out[k + SW + 1] + WD); if (i > 0) d = Math.min(d, out[k + SW - 1] + WD); }
    out[k] = d;
  }
}
// 有號距離場：mask 為 1 的地方是負的
function sdfOf(mask, SW, SH) {
  const NC = SW * SH, dOut = new Float32Array(NC), dIn = new Float32Array(NC), inv = new Uint8Array(NC), out = new Float32Array(NC);
  for (let k = 0; k < NC; k++) inv[k] = mask[k] ? 0 : 1;
  chamfer(mask, dOut, SW, SH); chamfer(inv, dIn, SW, SH);
  for (let k = 0; k < NC; k++) out[k] = mask[k] ? -(dIn[k] - 12) : dOut[k] - 12;
  return out;
}

export function fieldMap(D) { return (into) => build(D, into || {}); }

function build(D, M) {
  const W = D.W, H = D.H, YMIN = 200, YMAX = H - 40;
  const SW = Math.ceil(W / SC), SH = Math.ceil(H / SC), NC = SW * SH;
  Object.assign(M, { W, H, name: D.name, center: [W / 2, H / 2], campaign: D.id, BODY, rng, segDist });
  M.mir = (p) => p.slice(); // 列傳地圖不對稱
  const inBounds = (x, y) => x >= 60 && x <= W - 60 && y >= YMIN && y <= YMAX;
  const cellX = (k) => ((k % SW) + 0.5) * SC, cellY = (k) => (((k / SW) | 0) + 0.5) * SC;
  const cellAt = (x, y) => Math.max(0, Math.min(SH - 1, (y / SC) | 0)) * SW + Math.max(0, Math.min(SW - 1, (x / SC) | 0));

  // ---------- 地物 ----------
  M.roads = (D.roads || []).map((r) => ({ pts: smooth(r.pts, 6), w: r.w || 150 }));
  M.rivers = (D.rivers || []).map((r) => ({ pts: smooth(r.pts, 10), hw: r.hw || 140, fords: r.fords || [] }));
  M.bases = D.bases.map(([x, y]) => ({ x, y }));
  M.shopR = 420;
  M.marsh = D.marsh || [];
  M.brush = D.grass || [];
  M.fields = D.fields || [];
  M.rocks = D.rocks || [];
  M.clearings = D.clearings || [];
  // 引擎沿用的欄位：沒有城寨、兵營、糧倉、神秘商店、野怪營地；路線用第一條路（小兵波次在列傳裡關掉）
  Object.assign(M, { towers: [], barracks: [], granaries: [], shops: [], SHOP_R: {}, camps: [], glades: [], fords: [], shallows: M.marsh });
  M.shops = (D.shops || []).map(([x, y]) => [x, y, 'village']);
  M.SHOP_R = { village: 240 };
  M.shopAt = (x, y) => M.shops.some((p) => Math.hypot(x - p[0], (y - p[1]) * TY) < M.SHOP_R.village);
  M.shopHint = '回本營或到村裡的商舖才能購買';
  M.boss = { x: -9999, y: -9999 }; M.island = { x: -9999, y: -9999, r: 0 };
  M.lanes = { road: M.roads[0] ? M.roads[0].pts : [[M.bases[0].x, M.bases[0].y], [M.bases[1].x, M.bases[1].y]] };
  M.laneLen = {};
  for (const k in M.lanes) { let s = 0; const L = M.lanes[k]; for (let i = 0; i < L.length - 1; i++) s += Math.hypot(L[i + 1][0] - L[i][0], L[i + 1][1] - L[i][1]); M.laneLen[k] = s; }
  M.laneFor = (lane, team) => (team === 0 ? M.lanes[lane] : M.lanes[lane].slice().reverse());
  M.laneS = function (lane, x, y) {
    const L = M.lanes[lane]; let best = 0, bd = 1e12, acc = 0;
    for (let i = 0; i < L.length - 1; i++) {
      const a = L[i], b = L[i + 1], vx = b[0] - a[0], vy = b[1] - a[1], len = Math.hypot(vx, vy);
      const t = Math.max(0, Math.min(1, ((x - a[0]) * vx + (y - a[1]) * vy) / (len * len))), d = Math.hypot(x - a[0] - vx * t, y - a[1] - vy * t);
      if (d < bd) { bd = d; best = acc + t * len; }
      acc += len;
    }
    return best;
  };
  M.laneAt = function (lane, s) {
    const L = M.lanes[lane]; s = Math.max(0, Math.min(M.laneLen[lane], s));
    for (let i = 0; i < L.length - 1; i++) {
      const a = L[i], b = L[i + 1], len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (s <= len || i === L.length - 2) { const t = Math.min(1, s / len); return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }
      s -= len;
    }
    return L[L.length - 1].slice();
  };
  M.laneNear = () => 'road';
  const roadDist = (x, y) => { let d = 1e9; for (const r of M.roads) d = Math.min(d, lineDist(x, y, r.pts) - r.w / 2); return d; };
  const riverDist = (x, y) => { let d = 1e9; for (const r of M.rivers) d = Math.min(d, lineDist(x, y, r.pts) - r.hw); return d; }; // < 0 在水面上
  const fordAt = (x, y) => M.rivers.some((r) => r.fords.some((f) => Math.hypot(x - f[0], (y - f[1]) * 1.2) < f[2]));
  const onRoad = (x, y) => roadDist(x, y) < -10;
  M.onBridge = (x, y) => riverDist(x, y) < 40 && onRoad(x, y);
  // 橋：每條路連續落在河面（含岸邊 60）的那幾段，沿路再細切
  M.bridgeSpans = [];
  for (const rd of M.roads) {
    let cur = null;
    const P = rd.pts;
    for (let i = 0; i < P.length - 1; i++) for (let s = 0; s < 1; s += 0.1) {
      const p = [P[i][0] + (P[i + 1][0] - P[i][0]) * s, P[i][1] + (P[i + 1][1] - P[i][1]) * s];
      if (riverDist(p[0], p[1]) < 60) { if (!cur) M.bridgeSpans.push(cur = { w: rd.w, pts: [] }); cur.pts.push(p); } else cur = null;
    }
  }
  M.bridges = M.bridgeSpans.map((b) => { const c = b.pts[b.pts.length >> 1]; return { lane: 'road', pts: b.pts, x: c[0], y: c[1] }; });
  M.onFord = (x, y) => (riverDist(x, y) < 0 && fordAt(x, y) ? true : null);
  M.shallowAt = (x, y) => M.marsh.some((b) => inEll(x, y, b));
  M.waterAt = (x, y) => (riverDist(x, y) < 0 && !M.onBridge(x, y)) || M.shallowAt(x, y);
  // 和赤壁的介面相容：只有一條假的「江」放在地圖外（畫面、江霧、揚塵都會略過）；真正的河由 M.drawWater、M.waterAt 處理
  M.river = { a: [-1e5, -1e5], b: [-1e5 + 1, -1e5], hw: 0 };
  M.riverDist = () => 1e9;
  M.brushAt = function (x, y) { for (let i = 0; i < M.brush.length; i++) if (inEll(x, y, M.brush[i])) return i; return -1; };

  // ---------- 房舍與帳篷 ----------
  const R = rng(D.seed || 1);
  M.trees = [];
  const props = []; // 擋路的建物：{ x, y, hw, hh }（地面度量的半寬、半高）
  const spotFree = (x, y, rad) => inBounds(x, y) && roadDist(x, y) > rad && riverDist(x, y) > rad && !M.rocks.some((b) => inEll(x, y, [b[0], b[1], b[2] + rad, b[3] + rad])) && props.every((p) => Math.hypot(x - p.x, (y - p.y) * TY) > rad + Math.max(p.hw, p.hh) + 30);
  // 村裡的商舖（先放，房舍再繞開它）
  (D.shops || []).forEach(([x, y]) => props.push({ kind: 'shop', x, y, hw: 64, hh: 26 }));
  (D.villages || []).forEach((v) => {
    for (let n = 0, tries = 0; n < v.n && tries < 400; tries++) {
      const a = R() * Math.PI * 2, d = 120 + R() * (v.r || 520), x = v.x + Math.cos(a) * d, y = v.y + Math.sin(a) * d * 0.7;
      if (!spotFree(x, y, 90)) continue;
      const big = R() < 0.3;
      props.push({ kind: 'house', x, y, hw: big ? 90 : 70, hh: big ? 46 : 38, v: (R() * 3) | 0, big }); n++;
    }
  });
  (D.tents || []).forEach((v) => {
    for (let n = 0, tries = 0; n < v.n && tries < 400; tries++) {
      const a = R() * Math.PI * 2, d = 140 + R() * (v.r || 420), x = v.x + Math.cos(a) * d, y = v.y + Math.sin(a) * d * 0.7;
      if (!spotFree(x, y, 70)) continue;
      props.push({ kind: 'tent', x, y, hw: 52, hh: 30, big: n === 0 && v.chief, col: v.col, flag: v.flag }); n++; // col：帳篷布色、flag：大帳旗上的字（預設黃巾）
    }
  });
  // 城牆：[中心 x, 中心 y, 半寬, 半高]（地面度量），切成一段段依縱深畫；城門是牆上的缺口，由劇情的路障（m.gate）擋住
  M.cityWalls = D.walls || []; // M.walls 是技能造的牆（徐盛疑城等），別撞名
  for (const [cx, cy, hw, hh] of M.cityWalls) {
    const vert = hh > hw, len = vert ? hh : hw, n = Math.max(1, Math.round((len * 2) / 90));
    for (let i = 0; i < n; i++) {
      const t = -len + ((i + 0.5) * len * 2) / n, seg = len / n;
      props.push(vert ? { kind: 'wall', x: cx, y: cy + t, hw, hh: seg, vert } : { kind: 'wall', x: cx + t, y: cy, hw: seg, hh, vert });
    }
  }

  // ---------- 地形格 ----------
  const kind = new Uint8Array(NC);
  const nRock = noise((D.seed || 1) + 11, 140), nWood = noise((D.seed || 1) + 23, 260), nWood2 = noise((D.seed || 1) + 29, 90);
  const keepOpen = (x, y) => {
    // 樹林要讓開的空間（負的代表一定要空著）
    let s = Math.min(roadDist(x, y) - 70, riverDist(x, y) - 60);
    for (const b of M.bases) s = Math.min(s, Math.hypot(x - b.x, (y - b.y) * TY) - 700);
    for (const f of M.fields) if (Math.abs(x - f[0]) < f[2] / 2 + 60 && Math.abs(y - f[1]) < f[3] / 2 + 60) s = Math.min(s, -1);
    for (const c of M.clearings) s = Math.min(s, Math.hypot(x - c[0], (y - c[1]) * TY) - c[2]);
    for (const v of (D.villages || [])) s = Math.min(s, Math.hypot(x - v.x, (y - v.y) * TY) - (v.r || 520) - 260);
    for (const v of (D.tents || [])) s = Math.min(s, Math.hypot(x - v.x, (y - v.y) * TY) - (v.r || 420) - 260);
    for (const b of M.brush) if (inEll(x, y, [b[0], b[1], b[2] + 40, b[3] + 30])) s = Math.min(s, -1);
    for (const b of M.marsh) if (inEll(x, y, [b[0], b[1], b[2] + 40, b[3] + 30])) s = Math.min(s, -1);
    return s;
  };
  const woods = D.woods == null ? 0.5 : D.woods; // 0：幾乎沒有樹林、1：除了路旁都是林
  for (let k = 0; k < NC; k++) {
    const x = cellX(k), y = cellY(k);
    if (!inBounds(x, y)) { kind[k] = FOREST; continue; }
    if (riverDist(x, y) < 0 && !onRoad(x, y) && !fordAt(x, y)) { kind[k] = WATER; continue; }
    if (M.rocks.some((b) => ((x - b[0]) / b[2]) ** 2 + ((y - b[1]) / b[3]) ** 2 < 1 + nRock(x, y) * 0.35) && roadDist(x, y) > 40) { kind[k] = ROCK; continue; }
    const s = keepOpen(x, y), wv = nWood(x, y) * 0.75 + nWood2(x, y) * 0.25;
    if (s > 30 && wv > 0.55 - woods * 1.1) kind[k] = FOREST;
  }
  for (const p of props) {
    const i0 = Math.floor((p.x - p.hw) / SC), i1 = Math.ceil((p.x + p.hw) / SC);
    const j0 = Math.floor((p.kind === 'wall' ? p.y - p.hh : p.y - p.hh / TY * 1.4) / SC), j1 = Math.ceil((p.kind === 'wall' ? p.y + p.hh : p.y + p.hh / TY * 0.6) / SC);
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) if (i >= 0 && j >= 0 && i < SW && j < SH) kind[j * SW + i] = HOUSE;
  }
  // 太小的孤立樹叢拿掉
  {
    const lab = new Int32Array(NC).fill(-1);
    for (let k0 = 0; k0 < NC; k0++) {
      if (kind[k0] !== FOREST || lab[k0] >= 0) continue;
      const comp = [k0]; lab[k0] = k0;
      for (let h = 0; h < comp.length; h++) {
        const k = comp[h], i = k % SW, j = (k / SW) | 0;
        for (const [di, dj] of N4) { const a = i + di, b = j + dj, n = b * SW + a; if (a >= 0 && b >= 0 && a < SW && b < SH && kind[n] === FOREST && lab[n] < 0) { lab[n] = k0; comp.push(n); } }
      }
      if (comp.length < 14) comp.forEach((k) => { kind[k] = OPEN; });
    }
  }
  const mask = (pred) => { const m = new Uint8Array(NC); for (let k = 0; k < NC; k++) m[k] = pred(kind[k]) ? 1 : 0; return m; };
  let sdfMove = sdfOf(mask((t) => t !== OPEN), SW, SH);
  // 身體走不到的口袋（被林縫封死）填成林子，畫面與碰撞一致
  {
    const seen = new Uint8Array(NC), q = [];
    M.bases.forEach((b) => { const k = cellAt(b.x, b.y); seen[k] = 1; q.push(k); });
    while (q.length) {
      const k = q.pop(), i = k % SW, j = (k / SW) | 0;
      for (const [di, dj] of N4) { const a = i + di, b = j + dj, n = b * SW + a; if (a >= 0 && b >= 0 && a < SW && b < SH && !seen[n] && sdfMove[n] > BODY) { seen[n] = 1; q.push(n); } }
    }
    let filled = 0;
    for (let k = 0; k < NC; k++) if (kind[k] === OPEN && !seen[k] && sdfMove[k] > BODY && !M.shallowAt(cellX(k), cellY(k))) { kind[k] = FOREST; filled++; }
    if (filled) sdfMove = sdfOf(mask((t) => t !== OPEN), SW, SH);
  }
  const sdfSight = sdfOf(mask((t) => t === FOREST || t === ROCK || t === HOUSE), SW, SH);
  const sdfForest = sdfOf(mask((t) => t === FOREST), SW, SH), sdfRock = sdfOf(mask((t) => t === ROCK), SW, SH);
  const sampler = (f) => (x, y) => {
    const fx = Math.max(0, Math.min(SW - 1.001, x / SC - 0.5)), fy = Math.max(0, Math.min(SH - 1.001, y / SC - 0.5));
    const i = fx | 0, j = fy | 0, u = fx - i, v = fy - j, k = j * SW + i;
    return (f[k] * (1 - u) + f[k + 1] * u) * (1 - v) + (f[k + SW] * (1 - u) + f[k + SW + 1] * u) * v;
  };
  // 路障（劇情卡點：修橋、渡口水急）：開著的時候疊在走路用的距離場上，尋路格也跟著關
  const gates = new Map(), sMove0 = sampler(sdfMove);
  const sMove = (x, y) => { let d = sMove0(x, y); for (const g of gates.values()) d = Math.min(d, Math.hypot(x - g.x, (y - g.y) * TY) - g.r); return d; };
  const sSight = sampler(sdfSight), sForest = sampler(sdfForest), sRock = sampler(sdfRock);
  M.kindAt = (x, y) => kind[cellAt(x, y)];
  M.forestAt = (x, y) => sSight(x, y) < 0;
  M.forestDist = sSight;
  M.heightAt = () => 0.5;

  // ---------- 碰撞（pad > 0 是走路者；pad = 0 是投射物與視線，深水擋不住） ----------
  const S = (pad) => (pad > 0 ? sMove : sSight);
  M.walkable = (x, y, pad = 0) => inBounds(x, y) && S(pad)(x, y) > pad;
  M.segClear = function (x0, y0, x1, y1, pad = 0) {
    if (!inBounds(x1, y1)) return false;
    const f = S(pad), L = Math.hypot(x1 - x0, (y1 - y0) * TY);
    for (let t = 0; ;) {
      const k = L ? Math.min(1, t / L) : 1, d = f(x0 + (x1 - x0) * k, y0 + (y1 - y0) * k) - pad;
      if (d <= 0) return false;
      if (k >= 1) return true;
      t += Math.max(4, d * 0.9);
    }
  };
  M.pushOut = function (x, y, pad = 0) {
    const f = pad > 0 ? sMove : sSight;
    for (let it = 0; it < 8; it++) {
      const d = f(x, y);
      if (d > pad) break;
      const gx = (f(x + 4, y) - f(x - 4, y)) / 8, gy = (f(x, y + 4) - f(x, y - 4)) / (8 * TY), g = Math.hypot(gx, gy);
      if (g < 1e-4) break;
      const m = pad - d + 0.5;
      x += (gx / g) * m; y += (gy / g) * m / TY;
    }
    return [Math.max(60, Math.min(W - 60, x)), Math.max(YMIN, Math.min(YMAX, y))];
  };
  M.nearestFree = function (x, y, pad = BODY) {
    const p = M.pushOut(x, y, pad);
    if (M.walkable(p[0], p[1], pad)) return p;
    for (let r = SC; r <= 1400; r += SC) for (let a = 0; a < 24; a++) {
      const px = x + Math.cos((a * Math.PI) / 12) * r, py = y + Math.sin((a * Math.PI) / 12) * r * 0.7;
      if (M.walkable(px, py, pad)) return [px, py];
    }
    return [x, y];
  };

  // ---------- 尋路：40px 格子上的 A*，再拉直 ----------
  const PW = Math.ceil(W / CS), PH = Math.ceil(H / CS), free = new Uint8Array(PW * PH);
  for (let j = 0; j < PH; j++) for (let i = 0; i < PW; i++) { const x = (i + 0.5) * CS, y = (j + 0.5) * CS; free[j * PW + i] = M.walkable(x, y, BODY + 2) ? 1 : 0; }
  const pcell = (x, y) => [Math.max(0, Math.min(PW - 1, (x / CS) | 0)), Math.max(0, Math.min(PH - 1, (y / CS) | 0))];
  const nearFreeCell = (x, y) => {
    let [i, j] = pcell(x, y);
    if (free[j * PW + i]) return j * PW + i;
    for (let r = 1; r < 40; r++) for (let dj = -r; dj <= r; dj++) for (let di = -r; di <= r; di++) {
      if (Math.max(Math.abs(di), Math.abs(dj)) !== r) continue;
      const a = i + di, b = j + dj;
      if (a >= 0 && b >= 0 && a < PW && b < PH && free[b * PW + a]) return b * PW + a;
    }
    return -1;
  };
  // 開／關路障：id 相同就換掉；r 是地面度量的半徑。附近的尋路格重算
  M.setGate = function (id, x, y, r) {
    const old = gates.get(id);
    if (x == null) gates.delete(id); else gates.set(id, { x, y, r });
    for (const g of [old, gates.get(id)]) {
      if (!g) continue;
      const R = g.r + BODY + CS * 2, [i0, j0] = pcell(g.x - R, g.y - R), [i1, j1] = pcell(g.x + R, g.y + R);
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) free[j * PW + i] = M.walkable((i + 0.5) * CS, (j + 0.5) * CS, BODY + 2) ? 1 : 0;
    }
  };
  const gScore = new Float32Array(PW * PH), from = new Int32Array(PW * PH), stamp = new Uint32Array(PW * PH), closed = new Uint32Array(PW * PH);
  let gen = 0;
  M.path = function (x0, y0, x1, y1) {
    if (M.segClear(x0, y0, x1, y1, BODY)) return [[x1, y1]];
    const s = nearFreeCell(x0, y0), g = nearFreeCell(x1, y1);
    if (s < 0 || g < 0) return [[x1, y1]];
    gen++;
    const gx = g % PW, gy = (g / PW) | 0, hh = (k) => Math.hypot((k % PW) - gx, (((k / PW) | 0) - gy) * TY);
    const heap = [[hh(s), s]];
    const push = (f, k) => { heap.push([f, k]); let i = heap.length - 1; while (i > 0) { const p = (i - 1) >> 1; if (heap[p][0] <= heap[i][0]) break; [heap[p], heap[i]] = [heap[i], heap[p]]; i = p; } };
    const pop = () => { const top = heap[0], last = heap.pop(); if (heap.length) { heap[0] = last; let i = 0; for (;;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < heap.length && heap[l][0] < heap[m][0]) m = l; if (r < heap.length && heap[r][0] < heap[m][0]) m = r; if (m === i) break; [heap[m], heap[i]] = [heap[i], heap[m]]; i = m; } } return top; };
    stamp[s] = gen; gScore[s] = 0; from[s] = -1;
    let found = false;
    for (let it = 0; heap.length && it < 60000; it++) {
      const [, k] = pop();
      if (closed[k] === gen) continue;
      closed[k] = gen;
      if (k === g) { found = true; break; }
      const i = k % PW, j = (k / PW) | 0;
      for (const [di, dj] of N8) {
        const a = i + di, b = j + dj;
        if (a < 0 || b < 0 || a >= PW || b >= PH) continue;
        const n = b * PW + a;
        if (!free[n] || closed[n] === gen) continue;
        if (di && dj && (!free[j * PW + a] || !free[b * PW + i])) continue; // 斜走不切角
        const c = gScore[k] + Math.hypot(di, dj * TY);
        if (stamp[n] !== gen || c < gScore[n]) { stamp[n] = gen; gScore[n] = c; from[n] = k; push(c + hh(n), n); }
      }
    }
    if (!found) return [[x1, y1]];
    const cells = [];
    for (let k = g; k >= 0; k = from[k]) cells.unshift([((k % PW) + 0.5) * CS, (((k / PW) | 0) + 0.5) * CS]);
    cells.push([x1, y1]);
    // 拉直：從目前點往前找最遠一個直線走得到的點
    const out = [];
    let cur = [x0, y0], i = 0;
    while (i < cells.length - 1) {
      let j = cells.length - 1;
      while (j > i + 1 && !M.segClear(cur[0], cur[1], cells[j][0], cells[j][1], BODY)) j--;
      out.push(cells[j]); cur = cells[j]; i = j;
    }
    return out.length ? out : [[x1, y1]];
  };

  // ---------- 林緣的實體樹、房舍、帳篷（依縱深畫，能遮擋角色） ----------
  {
    const r = rng((D.seed || 1) + 5), taken = [];
    const facing = (x, y) => { const gx = sForest(x + 8, y) - sForest(x - 8, y), gy = sForest(x, y + 8) - sForest(x, y - 8); return gy / (Math.hypot(gx, gy) || 1); };
    const fits = (x, y) => { const d = sForest(x, y); return inBounds(x, y) && d < -8 && d > -36 && facing(x, y) > -0.25 && taken.every((t) => Math.hypot(x - t[0], (y - t[1]) * TY) > 70); };
    const deep = (x, y) => inBounds(x, y) && sForest(x, y) < -90 && taken.every((t) => Math.hypot(x - t[0], (y - t[1]) * TY) > 150);
    const order = [];
    for (let k = 0; k < NC; k++) if (kind[k] === FOREST && (sdfForest[k] > -40 || sdfForest[k] < -90)) order.push(k);
    for (let i = order.length - 1; i > 0; i--) { const j = (r() * (i + 1)) | 0; [order[i], order[j]] = [order[j], order[i]]; }
    for (const k of order) {
      const x = cellX(k) + (r() - 0.5) * SC, y = cellY(k) + (r() - 0.5) * SC;
      if (sdfForest[k] < -90 ? !deep(x, y) : !fits(x, y)) continue;
      taken.push([x, y]);
      M.trees.push({ x, y, s: 0.72 + ((((x * 7 + y * 13) | 0) % 40) + 40) % 40 / 120 });
    }
  }
  props.forEach((p) => M.trees.push(Object.assign({ s: 1 }, p)));

  // ---------- 繪製 ----------
  const nGround = noise((D.seed || 1) + 41, 900), nGround2 = noise((D.seed || 1) + 43, 260);
  const PAL = Object.assign({ grass: [77, 90, 54], dry: [112, 108, 64], road: '#8e7750', roadEdge: '#7b6746', water: '#2f5566', waterDeep: '#24485a', bank: '#7d7350', rock: '#6d6658', rockTop: '#8a8270', rockDark: '#3e3a33', field: '#7b7a42', fieldRow: 'rgba(60,62,28,.35)', marsh: '#4c6a5e' }, D.palette || {});
  const strokePts = (ctx, pts, w, col) => { ctx.strokeStyle = col; ctx.lineWidth = w; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.stroke(); };
  const near = (pts, x0, y0, w, h, pad) => pts.some((p) => p[0] > x0 - pad && p[0] < x0 + w + pad && p[1] > y0 - pad && p[1] < y0 + h + pad);
  function paintGround(ctx, x0, y0, w, h) {
    // 底色：大尺度雜訊在青草與乾草之間漸變；先畫在低解析度的小畫布上再放大，邊界平滑、沒有方塊接縫
    const G = 32, ox = Math.floor(x0 / G) * G - G, oy = Math.floor(y0 / G) * G - G, cw = Math.ceil(w / G) + 3, ch = Math.ceil(h / G) + 3;
    const cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
    const g = cv.getContext('2d'), img = g.createImageData(cw, ch), P = img.data;
    for (let j = 0; j < ch; j++) for (let i = 0; i < cw; i++) {
      const x = ox + i * G, y = oy + j * G, t = Math.max(0, Math.min(1, 0.5 + nGround(x, y) * 0.9 + nGround2(x, y) * 0.25)), k = (j * cw + i) * 4;
      for (let c = 0; c < 3; c++) P[k + c] = Math.round(PAL.grass[c] + (PAL.dry[c] - PAL.grass[c]) * t * 0.6);
      P[k + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    ctx.save(); ctx.imageSmoothingEnabled = true; ctx.drawImage(cv, ox - G / 2, oy - G / 2, cw * G, ch * G); ctx.restore();
    // 草斑與草葉：撒在全域的 128 見方格子上，畫一塊時連邊界外的格子一起畫，跨塊的草斑才接得起來
    const S = 128, cells = (pad, fn) => {
      for (let j = Math.floor((y0 - pad) / S); j <= Math.floor((y0 + h + pad) / S); j++) for (let i = Math.floor((x0 - pad) / S); i <= Math.floor((x0 + w + pad) / S); i++) fn(rng(i * 7919 + j * 104729 + (D.seed || 1)), i * S, j * S);
    };
    cells(110, (r, cx, cy) => {
      for (let n = 0; n < 4; n++) {
        ctx.fillStyle = r() < 0.5 ? 'rgba(90,108,58,.45)' : 'rgba(58,70,38,.4)';
        ctx.beginPath(); ctx.ellipse(cx + r() * S, cy + r() * S, 24 + r() * 70, 9 + r() * 26, 0, 0, Math.PI * 2); ctx.fill();
      }
    });
    ctx.lineWidth = 1.6; ctx.lineCap = 'round';
    cells(14, (r, cx, cy) => {
      for (let n = 0; n < 7; n++) {
        const x = cx + r() * S, y = cy + r() * S;
        ctx.strokeStyle = r() < 0.5 ? 'rgba(126,152,72,.5)' : 'rgba(46,62,30,.55)';
        ctx.beginPath(); ctx.moveTo(x - 4, y); ctx.lineTo(x - 1, y - 9); ctx.moveTo(x, y); ctx.lineTo(x + 2, y - 11); ctx.moveTo(x + 4, y); ctx.lineTo(x + 7, y - 8); ctx.stroke();
      }
    });
  }
  function paintFields(ctx, x0, y0, w, h) {
    for (const f of M.fields) {
      const [fx, fy, fw, fh] = f;
      if (fx + fw / 2 < x0 || fx - fw / 2 > x0 + w || fy + fh / 2 < y0 || fy - fh / 2 > y0 + h) continue;
      ctx.fillStyle = 'rgba(90,72,44,.55)'; ctx.fillRect(fx - fw / 2 - 14, fy - fh / 2 - 10, fw + 28, fh + 20); // 田埂
      ctx.fillStyle = f[4] || PAL.field; ctx.fillRect(fx - fw / 2, fy - fh / 2, fw, fh);
      ctx.strokeStyle = PAL.fieldRow; ctx.lineWidth = 3;
      for (let yy = fy - fh / 2 + 10; yy < fy + fh / 2; yy += 18) { ctx.beginPath(); ctx.moveTo(fx - fw / 2 + 6, yy); ctx.lineTo(fx + fw / 2 - 6, yy); ctx.stroke(); }
      ctx.strokeStyle = 'rgba(200,190,110,.25)'; ctx.lineWidth = 1.5;
      for (let yy = fy - fh / 2 + 16; yy < fy + fh / 2; yy += 18) { ctx.beginPath(); ctx.moveTo(fx - fw / 2 + 6, yy); ctx.lineTo(fx + fw / 2 - 6, yy); ctx.stroke(); }
    }
  }
  function paintWater(ctx, x0, y0, w, h) {
    for (const b of M.marsh) {
      if (b[0] + b[2] + 80 < x0 || b[0] - b[2] - 80 > x0 + w || b[1] + b[3] + 80 < y0 || b[1] - b[3] - 80 > y0 + h) continue;
      ctx.fillStyle = 'rgba(70,80,50,.6)'; ctx.beginPath(); ctx.ellipse(b[0], b[1], b[2] + 30, b[3] + 20, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = PAL.marsh; ctx.beginPath(); ctx.ellipse(b[0], b[1], b[2], b[3], 0, 0, Math.PI * 2); ctx.fill();
      const r = rng((b[0] * 3 + b[1]) | 0);
      ctx.strokeStyle = 'rgba(150,170,90,.7)'; ctx.lineWidth = 2;
      for (let i = 0; i < 40; i++) { const a = r() * Math.PI * 2, d = Math.sqrt(r()), x = b[0] + Math.cos(a) * b[2] * d, y = b[1] + Math.sin(a) * b[3] * d; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + (r() - 0.5) * 6, y - 14 - r() * 10); ctx.stroke(); }
    }
    for (const rv of M.rivers) {
      if (!near(rv.pts, x0, y0, w, h, rv.hw + 200)) continue;
      strokePts(ctx, rv.pts, rv.hw * 2 + 90, 'rgba(96,86,58,.7)');
      strokePts(ctx, rv.pts, rv.hw * 2 + 40, PAL.bank);
      strokePts(ctx, rv.pts, rv.hw * 2, PAL.water);
      strokePts(ctx, rv.pts, rv.hw * 1.1, PAL.waterDeep);
      for (const f of rv.fords) { // 渡口：只在河面上變淺（看得到河床），一排踏腳石橫過河
        ctx.save();
        ctx.beginPath(); rv.pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.lineWidth = rv.hw * 2; ctx.lineCap = 'butt';
        const g = ctx.createRadialGradient(f[0], f[1], f[2] * 0.2, f[0], f[1], f[2]);
        g.addColorStop(0, 'rgba(118,128,100,.85)'); g.addColorStop(0.7, 'rgba(96,116,104,.55)'); g.addColorStop(1, 'rgba(70,100,110,0)');
        ctx.strokeStyle = g; ctx.stroke();
        const r = rng((f[0] + f[1] * 7) | 0);
        for (let i = 0; i < 26; i++) {
          const a = r() * Math.PI * 2, d = Math.sqrt(r()) * f[2] * 0.75, x = f[0] + Math.cos(a) * d, y = f[1] + Math.sin(a) * d / 1.2;
          if (riverDist(x, y) > -6) continue;
          const rx = 8 + r() * 12, ry = 5 + r() * 6;
          ctx.fillStyle = 'rgba(40,40,30,.35)'; ctx.beginPath(); ctx.ellipse(x + 2, y + 3, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = r() < 0.5 ? '#8f8670' : '#a39878'; ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2); ctx.fill();
        }
        ctx.restore();
      }
    }
  }
  function paintRoads(ctx, x0, y0, w, h) {
    for (const rd of M.roads) {
      if (!near(rd.pts, x0, y0, w, h, rd.w + 200)) continue;
      strokePts(ctx, rd.pts, rd.w + 24, PAL.roadEdge); strokePts(ctx, rd.pts, rd.w, PAL.road);
      ctx.strokeStyle = 'rgba(60,44,28,.18)'; ctx.lineWidth = 3;
      for (const off of [-rd.w * 0.22, rd.w * 0.22]) {
        ctx.beginPath();
        rd.pts.forEach((p, i) => { const q = rd.pts[Math.min(i + 1, rd.pts.length - 1)], o = rd.pts[Math.max(i - 1, 0)], dx = q[0] - o[0], dy = q[1] - o[1], L = Math.hypot(dx, dy) || 1; const px = p[0] - (dy / L) * off, py = p[1] + (dx / L) * off; if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py); });
        ctx.stroke();
      }
    }
    // 橋：路面經過河的那一段鋪木板（平頭，兩端落在岸上）
    for (const br of M.bridgeSpans) {
      if (!near(br.pts, x0, y0, w, h, br.w + 300)) continue;
      ctx.save(); ctx.lineCap = 'butt'; ctx.lineJoin = 'round';
      for (const [wd, col] of [[br.w + 30, '#4a3424'], [br.w + 6, '#7a5634']]) { ctx.strokeStyle = col; ctx.lineWidth = wd; ctx.beginPath(); br.pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.stroke(); }
      ctx.strokeStyle = 'rgba(40,26,16,.5)'; ctx.lineWidth = 2;
      for (let i = 0; i < br.pts.length - 1; i++) {
        const a = br.pts[i], b = br.pts[i + 1], dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1, nx = -dy / L, ny = dx / L;
        for (let s = 0; s < L; s += 14) { const px = a[0] + (dx / L) * s, py = a[1] + (dy / L) * s; ctx.beginPath(); ctx.moveTo(px - nx * br.w * 0.56, py - ny * br.w * 0.56); ctx.lineTo(px + nx * br.w * 0.56, py + ny * br.w * 0.56); ctx.stroke(); }
      }
      ctx.restore();
    }
    // 營地與村落的空地
    for (const b of M.bases) { ctx.fillStyle = 'rgba(122,104,72,.85)'; ctx.beginPath(); ctx.ellipse(b.x, b.y, 520, 340, 0, 0, Math.PI * 2); ctx.fill(); }
    for (const v of (D.villages || [])) { ctx.fillStyle = 'rgba(128,108,74,.55)'; ctx.beginPath(); ctx.ellipse(v.x, v.y, (v.r || 520) + 120, ((v.r || 520) + 120) * 0.7, 0, 0, Math.PI * 2); ctx.fill(); }
    for (const v of (D.tents || [])) { ctx.fillStyle = 'rgba(132,112,70,.7)'; ctx.beginPath(); ctx.ellipse(v.x, v.y, (v.r || 420) + 120, ((v.r || 420) + 120) * 0.7, 0, 0, Math.PI * 2); ctx.fill(); }
  }
  function paintGrass(ctx, x0, y0, w, h) {
    // 高草叢：密集的草葉，躲在裡面的人敵方看不到
    for (const b of M.brush) {
      if (b[0] + b[2] + 40 < x0 || b[0] - b[2] - 40 > x0 + w || b[1] + b[3] + 40 < y0 || b[1] - b[3] - 40 > y0 + h) continue;
      ctx.fillStyle = 'rgba(52,70,30,.55)'; ctx.beginPath(); ctx.ellipse(b[0], b[1], b[2] + 14, b[3] + 10, 0, 0, Math.PI * 2); ctx.fill();
      const r = rng((b[0] * 7 + b[1] * 3) | 0), n = Math.round(b[2] * b[3] / 260);
      ctx.lineWidth = 2.2; ctx.lineCap = 'round';
      for (let i = 0; i < n; i++) {
        const a = r() * Math.PI * 2, d = Math.sqrt(r()), x = b[0] + Math.cos(a) * b[2] * d, y = b[1] + Math.sin(a) * b[3] * d, hgt = 18 + r() * 16;
        ctx.strokeStyle = r() < 0.5 ? '#8aa04e' : '#6d8a3e';
        ctx.beginPath(); ctx.moveTo(x, y); ctx.quadraticCurveTo(x + 3, y - hgt * 0.6, x + (r() - 0.3) * 10, y - hgt); ctx.stroke();
      }
    }
  }
  const CAN = 34, LIFT = 46;
  function paintBlocks(ctx, x0, y0, w, h, f, pal, rough) {
    // 林塊與岩塊共用：頂面圓塊、南緣的立面、往右下的投影
    const thin = (x, y) => f(x, y - LIFT) > -10;
    const i0 = Math.floor((x0 - 60) / CAN), i1 = Math.ceil((x0 + w + 60) / CAN), j0 = Math.floor((y0 - 60) / CAN), j1 = Math.ceil((y0 + h + 120) / CAN);
    const pts = [];
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const rr = rng(i * 92821 + j * 68917 + pal.seed), x = (i + rr()) * CAN, y = (j + rr()) * CAN, d = f(x, y);
      if (d > 70) continue;
      pts.push([x, y, d, thin(x, y) ? d : Math.max(d, f(x, y + LIFT)), rr(), rr(), rr()]);
    }
    if (!pts.length) return;
    for (const [x, y, d, , a] of pts) if (d > -10 && d < 60) {
      ctx.fillStyle = `rgba(12,20,10,${(0.2 * (1 - Math.max(0, d) / 60)).toFixed(3)})`;
      ctx.beginPath(); ctx.ellipse(x + 16, y + 14, 32 + a * 10, 20 + a * 6, 0, 0, Math.PI * 2); ctx.fill();
    }
    ctx.fillStyle = pal.floor;
    for (const [x, y, d] of pts) if (d < -4) { ctx.beginPath(); ctx.arc(x, y, Math.min(34, 10 + -d * 0.6), 0, Math.PI * 2); ctx.fill(); }
    // 南緣的立面（樹幹牆或岩壁）
    const cx0 = Math.floor((x0 - 20) / 11), cx1 = Math.ceil((x0 + w + 20) / 11);
    for (let ci = cx0; ci <= cx1; ci++) {
      const rr = rng(ci * 7919 + pal.seed), x = ci * 11 + rr() * 4, tw = rough ? 11 : 3 + rr() * 2.5, col = pal.face[(rr() * pal.face.length) | 0];
      if (!rough && rr() < 0.3) continue;
      for (let y = Math.floor((y0 - LIFT) / 5) * 5; y < y0 + h + 6; y += 5) {
        const d = f(x, y);
        if (d > -3 || f(x, y + LIFT) < -6 || thin(x, y)) continue;
        const q = Math.max(0, Math.min(1, (-f(x, y + LIFT) + 6) / 30));
        ctx.fillStyle = col; ctx.globalAlpha = 0.95 - q * 0.5; ctx.fillRect(x - tw / 2, y, tw, 5.5);
      }
    }
    ctx.globalAlpha = 1;
    const crowns = pts.filter((p) => p[3] < -8).map(([x, y, d, e, a, b, c]) => [x, y, Math.min(40, 14 + -e * 0.5) * (0.8 + a * 0.35), b, c]);
    if (rough) {
      // 岩塊：有稜角的石頭，左上受光面、右下背光面
      const stone = (x, y, rad, seed) => { const r = rng((seed * 1e6) | 0), n = 6, out = []; for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2 + r() * 0.5, d = rad * (0.75 + r() * 0.35); out.push([x + Math.cos(a) * d, y + Math.sin(a) * d * 0.8]); } return out; };
      const fillPoly = (P, col) => { ctx.fillStyle = col; ctx.beginPath(); P.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath(); ctx.fill(); };
      for (const [x, y, rad, b, c] of crowns) {
        const P = stone(x, y, rad, b + c);
        fillPoly(P.map(([px, py]) => [px + 5, py + 8]), pal.dark[(b * 3) | 0]);
        fillPoly(P, pal.mid[(b * 3) | 0]);
        fillPoly([P[3], P[4], P[5], [x, y]], pal.lite[(c * 3) | 0]); // 朝左上的受光面
        ctx.strokeStyle = 'rgba(30,26,20,.45)'; ctx.lineWidth = 1.6; ctx.beginPath(); P.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath(); ctx.stroke();
      }
    } else {
      for (const [x, y, rad, b] of crowns) { ctx.fillStyle = pal.dark[(b * 3) | 0]; ctx.beginPath(); ctx.arc(x + 4, y + 7, rad, 0, Math.PI * 2); ctx.fill(); }
      for (const [x, y, rad, b] of crowns) { ctx.fillStyle = pal.mid[(b * 3) | 0]; ctx.beginPath(); ctx.arc(x, y, rad * 0.86, 0, Math.PI * 2); ctx.fill(); }
      for (const [x, y, rad, b, c] of crowns) {
        ctx.fillStyle = pal.lite[(c * 3) | 0]; ctx.beginPath(); ctx.arc(x - rad * 0.22, y - rad * 0.26, rad * 0.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = pal.shine; ctx.beginPath(); ctx.arc(x - rad * 0.32, y - rad * 0.38, rad * 0.22, 0, Math.PI * 2); ctx.fill();
      }
      ctx.strokeStyle = 'rgba(12,20,10,.35)'; ctx.lineWidth = 1.6;
      for (const [x, y, rad] of crowns) { ctx.beginPath(); ctx.arc(x, y, rad * 0.86, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
      ctx.strokeStyle = pal.rim; ctx.lineWidth = 2.4;
      for (const [x, y, rad] of crowns) if (f(x, y - rad - 6) > -2) { ctx.beginPath(); ctx.arc(x, y, rad * 0.84, 1.15 * Math.PI, 1.85 * Math.PI); ctx.stroke(); }
    }
  }
  const WOOD = { seed: 3, floor: '#16221a', face: ['#3a2a1c', '#2c2015'], dark: ['#1a2a1b', '#1c2f1a', '#182818'], mid: ['#2b452c', '#2f4a2b', '#294227'], lite: ['#3d5f39', '#44673d', '#3a5934'], shine: 'rgba(200,225,150,.18)', rim: 'rgba(205,230,150,.32)' };
  const STONE = { seed: 17, floor: '#3a352d', face: ['#4a4237', '#3d362d', '#544a3c'], dark: ['#4a4438', '#4f483b', '#453f34'], mid: ['#6d6658', '#736b5c', '#676052'], lite: ['#8a8270', '#918874', '#837b69'], shine: 'rgba(235,225,200,.22)', rim: 'rgba(235,225,200,.35)' };
  M.paint = function (ctx, x0, y0, w, h) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x0, y0, w, h); ctx.clip();
    paintGround(ctx, x0, y0, w, h);
    paintFields(ctx, x0, y0, w, h);
    paintWater(ctx, x0, y0, w, h);
    paintRoads(ctx, x0, y0, w, h);
    paintGrass(ctx, x0, y0, w, h);
    paintBlocks(ctx, x0, y0, w, h, sRock, STONE, true);
    paintBlocks(ctx, x0, y0, w, h, sForest, WOOD, false);
    ctx.restore();
  };
  // 水面的流動亮紋：沿著每條河的曲線漂（view.drawWater 會呼叫）
  M.drawWater = function (ctx, x0, y0, vw, vh, T) {
    ctx.save(); ctx.lineCap = 'round';
    for (const rv of M.rivers) {
      const P = rv.pts;
      for (let i = 0; i < P.length - 1; i++) {
        const a = P[i], b = P[i + 1];
        if (Math.max(a[0], b[0]) < x0 - 200 || Math.min(a[0], b[0]) > x0 + vw + 200 || Math.max(a[1], b[1]) < y0 - 200 || Math.min(a[1], b[1]) > y0 + vh + 200) continue;
        const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1, ux = dx / L, uy = dy / L;
        for (let n = 0; n < 3; n++) {
          const seed = i * 13.7 + n * 97.31, lane = ((seed * 7.7) % (rv.hw * 1.6)) - rv.hw * 0.8, s = (seed * 3.1 + T * (40 + n * 9)) % L;
          const px = a[0] + ux * s - uy * lane, py = a[1] + uy * s + ux * lane, len = 18 + (n % 3) * 9;
          if (M.onBridge(px, py)) continue;
          ctx.strokeStyle = `rgba(215,235,245,${0.18 + 0.14 * Math.sin(T * 2 + seed)})`; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + ux * len, py + uy * len); ctx.stroke();
        }
      }
    }
    ctx.restore();
  };
  // 小地圖底圖（view.drawMini 會呼叫）：k 是世界座標到小地圖像素的比例
  M.drawMini = function (x, k) {
    const COL = ['#4a5634', '#26361f', '#6d6658', '#3f6173', '#8a6a48'];
    for (let j = 0; j < SH; j += 2) for (let i = 0; i < SW; i += 2) { const t = kind[j * SW + i]; if (t === OPEN) continue; x.fillStyle = COL[t]; x.fillRect(i * SC * k, j * SC * k, Math.ceil(2 * SC * k) + 0.5, Math.ceil(2 * SC * k) + 0.5); }
    x.lineCap = 'round'; x.lineJoin = 'round';
    for (const rd of M.roads) { x.strokeStyle = '#8e7750'; x.lineWidth = Math.max(2, rd.w * k); x.beginPath(); rd.pts.forEach((p, i) => (i ? x.lineTo(p[0] * k, p[1] * k) : x.moveTo(p[0] * k, p[1] * k))); x.stroke(); }
    x.fillStyle = '#3f6173'; M.marsh.forEach((b) => { x.beginPath(); x.ellipse(b[0] * k, b[1] * k, Math.max(2, b[2] * k), Math.max(1.5, b[3] * k), 0, 0, Math.PI * 2); x.fill(); });
  };

  // 樹、房舍、帳篷（view 依縱深呼叫）
  M.drawTree = function (ctx, t, alpha) {
    if (t.kind === 'house') return drawHouse(ctx, t, alpha);
    if (t.kind === 'tent') return drawTent(ctx, t, alpha);
    if (t.kind === 'shop') return drawShop(ctx, t, alpha);
    if (t.kind === 'wall') return drawWall(ctx, t, alpha);
    return drawTree(ctx, t, alpha);
  };
  M.drawCamp = () => {};
  return M;
}

function drawTree(ctx, t, alpha) {
  const s = t.s;
  ctx.save(); ctx.translate(t.x, t.y); ctx.scale(s, s);
  if (alpha < 1) ctx.globalAlpha = alpha;
  SG.draw.castShadow(ctx, 0, 4, 46, 13, 130, 0.3);
  const v = t.v == null ? (t.v = ((t.x * 13 + t.y * 7) | 0) % 3) : t.v;
  const blob = (x, y, r, c) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); SG.draw.ink(ctx, c, 2.4); };
  if (v === 2) {
    SG.draw.caps(ctx, [[[0, 4], [0, -40], 11, '#5b3d27']]);
    [[-46, -36, 46, 56], [-40, -66, 40, 84], [-32, -94, 32, 108], [-20, -118, 20, 132]].forEach(([x0, yb, x1, yt], i) => SG.draw.poly(ctx, [[x0, -yb], [0, -yt], [x1, -yb]], i % 2 ? '#2c4a2c' : '#345a32', 2.4));
  } else {
    SG.draw.caps(ctx, [[[0, 4], [0, -46], 12, '#5b3d27']]);
    if (v === 1) { blob(-30, -66, 28, '#2a4628'); blob(30, -68, 28, '#2a4628'); blob(-12, -100, 30, '#3a5a30'); blob(16, -98, 32, '#3f6436'); }
    else { blob(-24, -70, 30, '#2f4a2a'); blob(24, -72, 30, '#2f4a2a'); blob(0, -96, 36, '#3a5a30'); }
    ctx.fillStyle = 'rgba(14,26,14,.35)'; ctx.beginPath(); ctx.ellipse(0, -56, 44, 12, 0, 0, Math.PI); ctx.fill();
    ctx.fillStyle = 'rgba(180,210,130,.28)'; ctx.beginPath(); ctx.arc(-10, -108, 14, 0, Math.PI * 2); ctx.arc(14, -102, 8, 0, Math.PI * 2); ctx.fill();
  }
  ctx.restore();
}
// 農舍：夯土牆、黑瓦或茅草屋頂
function drawHouse(ctx, t, alpha) {
  const { poly } = SG.draw, w = t.hw, hh = t.big ? 70 : 58;
  ctx.save(); ctx.translate(t.x, t.y);
  if (alpha < 1) ctx.globalAlpha = Math.max(0.5, alpha);
  SG.draw.castShadow(ctx, 10, 6, w + 20, 22, 90, 0.35);
  const wall = ['#b39a72', '#a88f68', '#9c8762'][t.v], roof = ['#3b3a3a', '#5a4630', '#6f5a36'][t.v], roofHi = ['#55524f', '#7a6040', '#8c7448'][t.v];
  poly(ctx, [[-w, 0], [w, 0], [w, -hh], [-w, -hh]], wall, 2.2); // 正面牆
  poly(ctx, [[-14, 0], [14, 0], [14, -38], [-14, -38]], '#4a3424', 1.8); // 門
  if (t.big) { poly(ctx, [[-w + 18, -40], [-w + 46, -40], [-w + 46, -22], [-w + 18, -22]], '#3a2a1c', 1.4); poly(ctx, [[w - 46, -40], [w - 18, -40], [w - 18, -22], [w - 46, -22]], '#3a2a1c', 1.4); }
  poly(ctx, [[-w - 16, -hh + 4], [w + 16, -hh + 4], [w - 6, -hh - 46], [-w + 6, -hh - 46]], roof, 2.4); // 屋頂
  ctx.strokeStyle = roofHi; ctx.lineWidth = 2;
  for (let x = -w; x <= w; x += 14) { ctx.beginPath(); ctx.moveTo(x, -hh + 2); ctx.lineTo(x * 0.92, -hh - 44); ctx.stroke(); }
  poly(ctx, [[-w + 2, -hh - 46], [w - 2, -hh - 46], [w - 2, -hh - 52], [-w + 2, -hh - 52]], roofHi, 1.6); // 屋脊
  ctx.restore();
}
// 村裡的商舖：竹竿撐起的布篷、櫃檯上擺著陶罈與布匹，掛一面「商」字幌子
function drawShop(ctx, t, alpha) {
  const { poly, caps } = SG.draw;
  ctx.save(); ctx.translate(t.x, t.y);
  if (alpha < 1) ctx.globalAlpha = Math.max(0.5, alpha);
  SG.draw.castShadow(ctx, 6, 4, 80, 18, 80, 0.3);
  caps(ctx, [[[-60, 0], [-60, -86], 4, '#5b3d27'], [[60, 0], [60, -86], 4, '#5b3d27']]);
  poly(ctx, [[-56, 0], [56, 0], [56, -30], [-56, -30]], '#7a5634', 2); // 櫃檯
  poly(ctx, [[-56, -30], [56, -30], [52, -36], [-52, -36]], '#94704a', 1.6);
  [['#8a5a3a', -38], ['#6e4a30', -20]].forEach(([c, x]) => { ctx.beginPath(); ctx.ellipse(x, -46, 9, 11, 0, 0, Math.PI * 2); SG.draw.ink(ctx, c, 1.6); }); // 陶罈
  poly(ctx, [[2, -36], [26, -36], [26, -50], [2, -50]], '#3f6e8c', 1.4); poly(ctx, [[28, -36], [46, -36], [46, -46], [28, -46]], '#b0443a', 1.4); // 布匹
  poly(ctx, [[-74, -82], [74, -82], [64, -104], [-64, -104]], '#c9b48a', 2.2); // 布篷
  ctx.fillStyle = 'rgba(150,60,40,.55)'; for (let x = -60; x < 64; x += 24) ctx.fillRect(x, -103, 12, 21);
  caps(ctx, [[[66, -104], [66, -150], 3, '#5b3d27']]);
  poly(ctx, [[66, -148], [96, -148], [96, -112], [66, -112]], '#e9dcb8', 1.8); // 幌子
  ctx.fillStyle = '#7a2a18'; ctx.font = '900 22px "Noto Serif TC", serif'; ctx.textAlign = 'center'; ctx.fillText('商', 81, -122);
  ctx.restore();
}
// 黃巾帳篷：黃布、木樁；渠帥的大帳插「黃」字旗
// 城牆一段：夯土牆的正面、牆頂的走道與垛口（直的那段只看得到牆頂與側面）
function drawWall(ctx, t, alpha) {
  const { poly } = SG.draw, Hh = 96, w = t.hw, d = t.hh;
  ctx.save(); ctx.translate(t.x, t.y);
  if (alpha < 1) ctx.globalAlpha = Math.max(0.55, alpha);
  if (!t.vert) {
    poly(ctx, [[-w, d], [w, d], [w, d - Hh], [-w, d - Hh]], '#7a6a50', 0.01); // 正面
    ctx.strokeStyle = 'rgba(60,48,32,.35)'; ctx.lineWidth = 1.5;
    for (let y = d - Hh + 18; y < d; y += 18) { ctx.beginPath(); ctx.moveTo(-w, y); ctx.lineTo(w, y); ctx.stroke(); } // 夯土一層層
    poly(ctx, [[-w, d - Hh], [w, d - Hh], [w, -d - Hh], [-w, -d - Hh]], '#a8977a', 0.01); // 牆頂
    ctx.fillStyle = 'rgba(40,30,20,.35)'; ctx.fillRect(-w, d - Hh, w * 2, 5); // 牆頂邊緣的陰影
    for (let x = -w + 6; x < w - 4; x += 24) { ctx.fillStyle = '#6a5c46'; ctx.fillRect(x, d - Hh - 16, 14, 16); ctx.fillStyle = '#8c7c60'; ctx.fillRect(x, d - Hh - 16, 14, 4); } // 垛口
  } else {
    poly(ctx, [[-w, d - Hh], [w, d - Hh], [w, -d - Hh], [-w, -d - Hh]], '#a8987a', 0.01);
    poly(ctx, [[w, -d - Hh], [w, d - Hh], [w, d], [w, -d]], '#7a6c52', 0.01);
    for (let y = -d + 6; y < d - 4; y += 24) { ctx.fillStyle = '#6a5c46'; ctx.fillRect(-w, y - Hh - 12, 12, 14); }
  }
  ctx.restore();
}
function drawTent(ctx, t, alpha) {
  const { poly, caps } = SG.draw, k = t.big ? 1.35 : 1;
  ctx.save(); ctx.translate(t.x, t.y); ctx.scale(k, k);
  if (alpha < 1) ctx.globalAlpha = Math.max(0.5, alpha);
  SG.draw.castShadow(ctx, 6, 4, 64, 18, 80, 0.3);
  poly(ctx, [[-56, 0], [56, 0], [0, -78]], t.col || '#d9b444', 2.4);
  poly(ctx, [[-14, 0], [14, 0], [0, -40]], '#5a4220', 1.8);
  ctx.strokeStyle = 'rgba(120,90,20,.55)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-28, -38); ctx.lineTo(-10, 0); ctx.moveTo(28, -38); ctx.lineTo(10, 0); ctx.stroke();
  if (t.big) {
    caps(ctx, [[[40, 0], [40, -130], 3, '#5b3d27']]);
    poly(ctx, [[40, -128], [86, -122], [80, -100], [40, -96]], t.col ? '#e9dcb8' : '#e2c040', 2);
    ctx.fillStyle = '#5a2a10'; ctx.font = '900 22px "Noto Serif TC", serif'; ctx.textAlign = 'center'; ctx.fillText(t.flag || '黃', 61, -104);
  }
  ctx.restore();
}
